import { useState } from "react";
import AppLayout from "@/components/feature/AppLayout";

const integrations = [
  { id: "slack", name: "Slack", desc: "Send conversation notifications to Slack channels.", icon: "ri-slack-line", color: "#4A154B", connected: true, category: "Communication" },
];

export default function IntegrationsPage() {
  const [connected, setConnected] = useState(Object.fromEntries(integrations.map((i) => [i.id, i.connected])));
  const [search, setSearch] = useState("");

  const filtered = integrations.filter(
    (i) => i.name.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase()),
  );

  const categories = [...new Set(integrations.map((i) => i.category))];
  const nConnected = Object.values(connected).filter(Boolean).length;

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin h-full overflow-y-auto">
        <header className="ui-shell sticky top-0 z-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">Ecosystem</p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Integrations</h1>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">
                Connect tools your team already uses. OAuth-ready cards show live connection state.
              </p>
            </div>
            <div className="relative w-full max-w-md shrink-0">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-slate-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by app or category…"
                className="ui-input !pl-10"
                aria-label="Search integrations"
              />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800 ring-1 ring-teal-200/60">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
              {nConnected} live
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
              {integrations.length} available
            </span>
          </div>
        </header>

        <div className="space-y-10 px-6 py-8 sm:px-8">
          {categories.map((cat) => {
            const items = filtered.filter((i) => i.category === cat);
            if (!items.length) return null;
            return (
              <section key={cat}>
                <h2 className="mb-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">{cat}</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {items.map((intg) => (
                    <article
                      key={intg.id}
                      className="ui-card group flex flex-col gap-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div
                          className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-inner ring-1 ring-black/[0.04] transition-transform duration-300 group-hover:scale-105"
                          style={{ backgroundColor: `${intg.color}18` }}
                        >
                          <i className={`${intg.icon} text-2xl`} style={{ color: intg.color }} aria-hidden />
                        </div>
                        {connected[intg.id] && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200/70">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                            Live
                          </span>
                        )}
                      </div>
                      <div className="min-h-0 flex-1">
                        <h3 className="text-base font-semibold text-slate-900">{intg.name}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{intg.desc}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setConnected((p) => ({ ...p, [intg.id]: !p[intg.id] }))}
                        className={`mt-auto w-full rounded-xl py-2.5 text-xs font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                          connected[intg.id]
                            ? "border border-rose-200/90 bg-rose-50/90 text-rose-700 hover:bg-rose-100/90 focus-visible:ring-rose-400"
                            : "ui-btn-primary"
                        }`}
                      >
                        {connected[intg.id] ? "Disconnect" : "Connect"}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
